import { createApi } from '@reduxjs/toolkit/query/react'
import { BACKEND_API_URL, urlApi } from '../consts/api'

import { extractResponseData, extractResponseError, fetchBaseQueryWithToken } from '../utils/api'
import type {
  TActiveUserResponse,
  TGetUsersCursorQueryParams,
  TGetUsersQueryParams,
  TUserResponse,
  TUsersCursorPage,
  TUsersListResponse,
  TUsersStatsResponse,
} from './types'
import { getMediaSourceUrl } from '../utils/media'

export const usersTag = 'usersTag'
type TFollowUserPayload = { userId: number }

function parseUsersListResponse(response: unknown): TUsersListResponse {
  const data = extractResponseData(response)
  const normalizeListItem = (u: TUsersListResponse[number]): TUsersListResponse[number] => ({
    ...u,
    avatar: getMediaSourceUrl((u as { avatar?: string | null }).avatar),
    competencies: Array.isArray(u.competencies) ? u.competencies : [],
    improvements: Array.isArray(u.improvements) ? u.improvements : [],
    resources: Array.isArray(u.resources) ? u.resources : [],
    participation: Array.isArray(u.participation) ? u.participation : [],
    shortname: u.shortname ?? '',
    slug: u.slug ?? '',
    is_followed: Boolean(u.is_followed),
  })

  if (Array.isArray(data)) {
    return (data as TUsersListResponse).map(normalizeListItem)
  }

  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return ((data as { data: TUsersListResponse }).data).map(normalizeListItem)
  }

  if (response && typeof response === 'object' && Array.isArray((response as { data?: unknown }).data)) {
    return ((response as { data: TUsersListResponse }).data).map(normalizeListItem)
  }

  return []
}

function pickCursorFromNextLink(next: string): string | null {
  try {
    const fromUrl = new URL(next, BACKEND_API_URL).searchParams.get('cursor')
    if (fromUrl) {
      return fromUrl
    }
  } catch {
    // fall through to regex
  }

  const match = next.match(/[?&]cursor=([^&]+)/)
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

function pickNextCursorFromPageRoot(body: Record<string, unknown>): string | null {
  const meta = body.meta as Record<string, unknown> | undefined
  const fromMeta = meta?.next_cursor
  if (fromMeta !== undefined && fromMeta !== null && String(fromMeta) !== '') {
    return String(fromMeta)
  }

  const links = body.links as Record<string, unknown> | undefined
  const next = links?.next
  if (typeof next === 'string' && next.length > 0) {
    return pickCursorFromNextLink(next)
  }

  return null
}

function parseUsersCursorPageResponse(response: unknown): TUsersCursorPage {
  const top = (response ?? {}) as Record<string, unknown>
  const data = top.data

  if (Array.isArray(data)) {
    return {
      items: parseUsersListResponse(response),
      meta: { next_cursor: pickNextCursorFromPageRoot(top) },
    }
  }

  if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>).data)) {
    const root = data as Record<string, unknown>
    return {
      items: parseUsersListResponse(response),
      meta: { next_cursor: pickNextCursorFromPageRoot(root) },
    }
  }

  return {
    items: parseUsersListResponse(response),
    meta: { next_cursor: null },
  }
}

function serializeUsersQueryParams(arg: TGetUsersCursorQueryParams | void): Record<string, string> {
  const raw = arg ?? {}
  const params: Record<string, string> = {}

  const query = raw.query?.trim()
  if (query) params.query = query

  const status = raw.status?.trim()
  if (status && status !== 'all') params.status = status

  const city = raw.city?.trim()
  if (city && city !== 'all') params.city = city

  if (typeof raw.age_from === 'number' && Number.isFinite(raw.age_from)) {
    params.age_from = String(raw.age_from)
  }
  if (typeof raw.age_to === 'number' && Number.isFinite(raw.age_to)) {
    params.age_to = String(raw.age_to)
  }
  if (raw.cursor) {
    params.cursor = raw.cursor
  }
  if (typeof raw.limit === 'number' && Number.isFinite(raw.limit)) {
    params.limit = String(raw.limit)
  }
  if (raw.sort_by) {
    params.sort_by = raw.sort_by
  }
  if (raw.sort_direction) {
    params.sort_direction = raw.sort_direction
  }

  return params
}

function parseUserByIdResponse(response: unknown): TUserResponse {
  const u = extractResponseData(response) as TUserResponse & { deleted?: boolean }
  const deleted = u.deleted === true

  if (deleted) {
    return {
      ...(u as TUserResponse),
      deleted: true,
      id: null,
      slug: null,
      avatar: null,
      counters: {
        projects: Number(u.counters?.projects ?? 0),
        teams: Number(u.counters?.teams ?? 0),
        comments: Number(u.counters?.comments ?? 0),
        subscriptions: Number(u.counters?.subscriptions ?? 0),
        subscribers: Number(u.counters?.subscribers ?? 0),
      },
    } as TUserResponse
  }

  return {
    ...(u as TActiveUserResponse),
    avatar: getMediaSourceUrl((u as any).avatar),
    counters: {
      projects: Number(u.counters?.projects ?? 0),
      teams: Number(u.counters?.teams ?? 0),
      comments: Number(u.counters?.comments ?? 0),
      subscriptions: Number(u.counters?.subscriptions ?? 0),
      subscribers: Number(u.counters?.subscribers ?? 0),
    },
  }
}

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: fetchBaseQueryWithToken(BACKEND_API_URL),
  tagTypes: [usersTag],
  endpoints: (builder) => ({
    getUsers: builder.query<TUsersListResponse, TGetUsersQueryParams>({
      query: (arg) => ({
        url: `/${urlApi.users}`,
        params: arg,
      }),
      transformResponse: (response: unknown) => parseUsersListResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) =>
        result
          ? [
              usersTag,
              { type: usersTag, id: 'LIST' },
              ...result.map((user) => ({ type: usersTag, id: user.id } as const)),
            ]
          : [usersTag, { type: usersTag, id: 'LIST' }],
    }),
    getUsersPage: builder.query<TUsersCursorPage, TGetUsersCursorQueryParams | void>({
      query: (arg) => {
        const params = serializeUsersQueryParams(arg)
        return {
          url: `/${urlApi.users}`,
          ...(Object.keys(params).length > 0 ? { params } : {}),
        }
      },
      transformResponse: (response: unknown) => parseUsersCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: [usersTag, { type: usersTag, id: 'LIST' }],
    }),

    getMyFollowing: builder.query<TUsersListResponse, void>({
      query: () => ({
        url: `/${urlApi.users}/me/follows`,
      }),
      transformResponse: (response: unknown) => parseUsersListResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) =>
        result
          ? [
              usersTag,
              { type: usersTag, id: 'FOLLOWING_LIST' },
              ...result.map((user) => ({ type: usersTag, id: user.id } as const)),
            ]
          : [usersTag, { type: usersTag, id: 'FOLLOWING_LIST' }],
    }),
    getMyFollowingPage: builder.query<TUsersCursorPage, TGetUsersCursorQueryParams | void>({
      query: (arg) => {
        const params = serializeUsersQueryParams(arg)
        return {
          url: `/${urlApi.users}/me/follows`,
          ...(Object.keys(params).length > 0 ? { params } : {}),
        }
      },
      transformResponse: (response: unknown) => parseUsersCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: [usersTag, { type: usersTag, id: 'FOLLOWING_LIST' }],
    }),

    getMyFollowersPage: builder.query<TUsersCursorPage, TGetUsersCursorQueryParams | void>({
      query: (arg) => {
        const params = serializeUsersQueryParams(arg)
        return {
          url: `/${urlApi.users}/me/followers`,
          ...(Object.keys(params).length > 0 ? { params } : {}),
        }
      },
      transformResponse: (response: unknown) => parseUsersCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: [usersTag, { type: usersTag, id: 'FOLLOWERS_LIST' }],
    }),

    getUsersStats: builder.query<TUsersStatsResponse, void>({
      query: () => ({
        url: `/${urlApi.users}/stats`,
      }),
      transformResponse: (response: unknown) => {
        const data = extractResponseData(response) as Partial<TUsersStatsResponse> | null
        return {
          profiles_completed_count: Number(data?.profiles_completed_count ?? 0),
          profiles_incomplete_count: Number(data?.profiles_incomplete_count ?? 0),
        }
      },
      transformErrorResponse: extractResponseError,
      providesTags: [usersTag, { type: usersTag, id: 'STATS' }],
    }),

    getUserById: builder.query<TUserResponse, string | number>({
      query: (slugOrId) => ({
        url: `/${urlApi.users}/${slugOrId}`,
      }),
      transformResponse: (response: unknown) => parseUserByIdResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result, error, slugOrId) =>
        result
          ? [
              usersTag,
              ...(result.id != null ? [{ type: usersTag, id: result.id } as const] : []),
              { type: usersTag, id: slugOrId },
            ]
          : [usersTag, { type: usersTag, id: slugOrId }],
    }),

    followUser: builder.mutation<void, TFollowUserPayload>({
      query: ({ userId }) => ({
        url: `/${urlApi.users}/${userId}/follow`,
        method: 'POST',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (result, error, { userId }) => [
        usersTag,
        { type: usersTag, id: 'LIST' },
        { type: usersTag, id: 'FOLLOWING_LIST' },
        { type: usersTag, id: 'FOLLOWERS_LIST' },
        { type: usersTag, id: userId },
      ],
    }),

    unfollowUser: builder.mutation<void, TFollowUserPayload>({
      query: ({ userId }) => ({
        url: `/${urlApi.users}/${userId}/follow`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (result, error, { userId }) => [
        usersTag,
        { type: usersTag, id: 'LIST' },
        { type: usersTag, id: 'FOLLOWING_LIST' },
        { type: usersTag, id: 'FOLLOWERS_LIST' },
        { type: usersTag, id: userId },
      ],
    }),

    deleteUser: builder.mutation<void, { userId: number }>({
      query: ({ userId }) => ({
        url: `/${urlApi.users}/${userId}/delete`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
    }),
  }),
})

export const {
  useGetUsersQuery,
  useGetUsersPageQuery,
  useLazyGetUsersPageQuery,
  useGetUsersStatsQuery,
  useGetMyFollowingQuery,
  useGetMyFollowingPageQuery,
  useLazyGetMyFollowingPageQuery,
  useLazyGetMyFollowersPageQuery,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useDeleteUserMutation,
} = usersApi
